import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { 
  Loader2, CheckCircle, XCircle, AlertTriangle, Eye, Bell, 
  Plus, Trash2, RefreshCw, Clock, ShieldAlert, ShieldCheck, 
  Mail, Sparkles, ExternalLink, Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_COLORS = {
  critical: "bg-red-500/20 text-red-600 border-red-500/30",
  high: "bg-orange-500/20 text-orange-600 border-orange-500/30",
  medium: "bg-yellow-500/20 text-yellow-600 border-yellow-500/30",
  info: "bg-blue-500/20 text-blue-600 border-blue-500/30",
};

const LinkMonitorTab = () => {
  const [monitors, setMonitors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loadingMonitors, setLoadingMonitors] = useState(false);
  const [checkingAll, setCheckingAll] = useState(false);
  const [newMonitor, setNewMonitor] = useState({ page_url: "", target_url: "", notes: "" });
  const [addingMonitor, setAddingMonitor] = useState(false);
  const [emailConfig, setEmailConfig] = useState({ email: "", enabled: false });
  const [savingEmail, setSavingEmail] = useState(false);
  const [lastCheckInfo, setLastCheckInfo] = useState(null);

  // Domain Health State
  const [healthResults, setHealthResults] = useState([]);
  const [healthSummary, setHealthSummary] = useState(null);
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthProgress, setHealthProgress] = useState(null);
  const [healthFilter, setHealthFilter] = useState("down");
  const [selectedDown, setSelectedDown] = useState(new Set());
  const [deactivating, setDeactivating] = useState(false);
  const [removingFromList, setRemovingFromList] = useState(false);
  const healthFileRef = React.useRef(null);

  const loadMonitors = useCallback(async () => {
    setLoadingMonitors(true);
    try {
      const response = await fetch(`${API}/link-monitor/list`);
      const data = await response.json();
      setMonitors(data.monitors || []);
    } catch (error) {
      toast.error("Error al cargar monitores");
    }
    setLoadingMonitors(false);
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/link-monitor/alerts`);
      const data = await response.json();
      setAlerts(data.alerts || []);
    } catch (error) {
      console.error("Error loading alerts:", error);
    }
  }, []);

  const checkAutoCheck = useCallback(async () => {
    try {
      const response = await fetch(`${API}/link-monitor/should-auto-check`);
      const data = await response.json();
      setLastCheckInfo(data);
      
      if (data.should_check && data.monitors_count > 0) {
        toast.info(`Verificando ${data.monitors_count} enlaces automáticamente...`);
        await checkAllMonitors();
      }
    } catch (error) {
      console.error("Error checking auto-check status:", error);
    }
  }, []);

  const loadEmailConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API}/link-monitor/email-config`);
      const data = await response.json();
      if (data.email) {
        setEmailConfig(data);
      }
    } catch (error) {
      console.error("Error loading email config:", error);
    }
  }, []);

  useEffect(() => {
    loadMonitors();
    loadAlerts();
    loadEmailConfig();
    checkAutoCheck();
  }, [loadMonitors, loadAlerts, loadEmailConfig, checkAutoCheck]);

  const saveEmailConfig = async () => {
    if (!emailConfig.email) {
      toast.error("Introduce un email");
      return;
    }
    
    setSavingEmail(true);
    try {
      const response = await fetch(`${API}/link-monitor/email-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailConfig)
      });
      
      if (response.ok) {
        toast.success("Email guardado correctamente");
      }
    } catch (error) {
      toast.error("Error al guardar email");
    }
    setSavingEmail(false);
  };

  const addMonitor = async () => {
    if (!newMonitor.page_url || !newMonitor.target_url) {
      toast.error("Introduce la URL de la página y el enlace a monitorear");
      return;
    }
    
    setAddingMonitor(true);
    try {
      const response = await fetch(`${API}/link-monitor/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: newMonitor.page_url,
          target_url: newMonitor.target_url,
          notes: newMonitor.notes
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Enlace añadido al monitor");
        setNewMonitor({ page_url: "", target_url: "", notes: "" });
        loadMonitors();
      } else {
        toast.error(data.detail || "Error al añadir monitor");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setAddingMonitor(false);
  };

  const checkAllMonitors = async () => {
    setCheckingAll(true);
    try {
      const response = await fetch(`${API}/link-monitor/check-all`, {
        method: "POST"
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.alerts_count > 0) {
          toast.warning(`¡${data.alerts_count} alertas detectadas!`);
        } else {
          toast.success(`${data.checked} enlaces verificados. Sin cambios.`);
        }
        loadMonitors();
        loadAlerts();
      }
    } catch (error) {
      toast.error("Error al verificar");
    }
    setCheckingAll(false);
  };

  const deleteMonitor = async (monitorId) => {
    if (!window.confirm("¿Eliminar este monitor?")) return;
    
    try {
      const response = await fetch(`${API}/link-monitor/${monitorId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Monitor eliminado");
        loadMonitors();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await fetch(`${API}/link-monitor/export`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `monitor_enlaces_${new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel descargado");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Error al descargar");
      }
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  // Domain Health Functions
  const loadHealthResults = useCallback(async (filter = healthFilter) => {
    try {
      const response = await fetch(`${API}/link-monitor/domain-health/results?filter=${filter}`);
      if (response.ok) {
        const data = await response.json();
        setHealthResults(data.results || []);
        setHealthSummary(data.summary || null);
      }
    } catch (error) {
      console.error("Error loading health results:", error);
    }
  }, [healthFilter]);

  const startHealthCheck = async () => {
    setHealthChecking(true);
    try {
      const response = await fetch(`${API}/link-monitor/domain-health/check`, { method: "POST" });
      const data = await response.json();
      if (data.status === "started") {
        toast.success(`Verificando ${data.total || 'todos los'} dominios...`);
        pollHealthProgress();
      } else if (data.status === "already_running") {
        toast.info("Ya hay una verificación en curso");
        pollHealthProgress();
      }
    } catch (error) {
      toast.error("Error al iniciar verificación");
      setHealthChecking(false);
    }
  };

  const pollHealthProgress = () => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/link-monitor/domain-health/status`);
        const data = await response.json();
        setHealthProgress(data);
        if (!data.in_progress) {
          clearInterval(interval);
          setHealthChecking(false);
          setHealthProgress(null);
          loadHealthResults();
          toast.success(`Verificación completada: ${data.errors} dominios con problemas`);
        }
      } catch (e) {
        clearInterval(interval);
        setHealthChecking(false);
      }
    }, 2000);
  };

  const uploadHealthExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setHealthChecking(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${API}/link-monitor/domain-health/upload-check`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (data.status === "started") {
        toast.success(`Verificando ${data.total} dominios del Excel...`);
        pollHealthProgress();
      }
    } catch (error) {
      toast.error("Error al subir archivo");
      setHealthChecking(false);
    }
    if (healthFileRef.current) healthFileRef.current.value = "";
  };

  const toggleDownSelection = (domain) => {
    const ns = new Set(selectedDown);
    if (ns.has(domain)) ns.delete(domain);
    else ns.add(domain);
    setSelectedDown(ns);
  };

  const selectAllDown = () => {
    const downDomains = healthResults.filter(r => r.status !== "ok").map(r => r.domain);
    if (selectedDown.size === downDomains.length) {
      setSelectedDown(new Set());
    } else {
      setSelectedDown(new Set(downDomains));
    }
  };

  const deactivateSelected = async () => {
    if (selectedDown.size === 0) return;
    setDeactivating(true);
    try {
      const response = await fetch(`${API}/link-monitor/domain-health/deactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains: Array.from(selectedDown) }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${data.deactivated} productos desactivados`);
        setSelectedDown(new Set());
        loadHealthResults();
      }
    } catch (error) {
      toast.error("Error al desactivar");
    } finally {
      setDeactivating(false);
    }
  };

  const downloadHealthSelected = async () => {
    if (selectedDown.size === 0) return;
    try {
      const response = await fetch(`${API}/link-monitor/domain-health/export-selected`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains: Array.from(selectedDown) }),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dominios_seleccionados_${new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel descargado");
      }
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  const removeFromHealthList = async () => {
    if (selectedDown.size === 0) return;
    setRemovingFromList(true);
    try {
      const response = await fetch(`${API}/link-monitor/domain-health/remove-from-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains: Array.from(selectedDown) }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${data.removed} dominios eliminados del listado`);
        setSelectedDown(new Set());
        loadHealthResults();
      }
    } catch (error) {
      toast.error("Error al eliminar del listado");
    } finally {
      setRemovingFromList(false);
    }
  };

  useEffect(() => {
    loadHealthResults();
  }, [loadHealthResults]);

  return (
    <div className="space-y-6" data-testid="link-monitor-tab">
      {/* Hero Info Section */}
      <Card className="bg-gradient-to-br from-purple-500/10 to-violet-500/5 border-purple-500/20 overflow-hidden">
        <CardContent className="pt-6 pb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-400 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Monitor de Enlaces</h3>
                  <p className="text-sm text-purple-600">Vigilancia automática de tus backlinks</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Añade los enlaces que quieres vigilar y nosotros los verificamos 
                <span className="text-purple-600 font-medium"> automáticamente cada 24h</span>. 
                Recibe alertas cuando un enlace cambie de estado o desaparezca.
              </p>
              {lastCheckInfo && lastCheckInfo.hours_since_last_check && (
                <p className="text-sm text-blue-600">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Última verificación: hace {lastCheckInfo.hours_since_last_check}h
                </p>
              )}
            </div>
            
            <div className="space-y-3 bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-500" />
                Perfecto para
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2 text-gray-600">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                  <span><strong className="text-gray-800">Backlinks:</strong> Monitorea que tus enlaces conseguidos sigan activos</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                  <span><strong className="text-gray-800">Guest Posts:</strong> Verifica que tus artículos invitados mantienen los enlaces</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                  <span><strong className="text-gray-800">Intercambios:</strong> Confirma que los acuerdos de intercambio se mantienen</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Config */}
      <Card className="border-blue-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-500" />
            Alertas por Email
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="tu@email.com"
              value={emailConfig.email}
              onChange={(e) => setEmailConfig(prev => ({ ...prev, email: e.target.value }))}
              className="flex-1"
            />
            <Button 
              onClick={saveEmailConfig} 
              disabled={savingEmail || !emailConfig.email}
              size="sm"
            >
              {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="border-red-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="w-5 h-5" />
              Alertas Activas ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {alerts.slice(0, 10).map((alert, idx) => (
                <div key={idx} className={`p-3 rounded-lg border ${ALERT_COLORS[alert.severity]}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{alert.message}</p>
                      <p className="text-xs mt-1 opacity-70 truncate max-w-md">{alert.target_url}</p>
                    </div>
                    <span className="text-xs opacity-50">
                      {alert.detected_at ? new Date(alert.detected_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Monitor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-500" />
            Añadir Enlace a Monitorear
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                URL de la página que contiene el enlace
              </label>
              <Input
                placeholder="https://ejemplo.com/articulo-con-mi-enlace"
                value={newMonitor.page_url}
                onChange={(e) => setNewMonitor(prev => ({ ...prev, page_url: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                URL del enlace a monitorear (tu enlace)
              </label>
              <Input
                placeholder="https://midominio.com"
                value={newMonitor.target_url}
                onChange={(e) => setNewMonitor(prev => ({ ...prev, target_url: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Notas (opcional)</label>
            <Input
              placeholder="Ej: Guest post en blog X, publicado en enero 2025"
              value={newMonitor.notes}
              onChange={(e) => setNewMonitor(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>
          <Button onClick={addMonitor} disabled={addingMonitor} className="w-full">
            {addingMonitor ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Añadir al Monitor
          </Button>
        </CardContent>
      </Card>

      {/* Monitors List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-500" />
              Enlaces Monitoreados ({monitors.length})
            </span>
            <div className="flex items-center gap-2">
              <Button onClick={downloadExcel} disabled={monitors.length === 0} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Descargar Excel
              </Button>
              <Button onClick={checkAllMonitors} disabled={checkingAll || monitors.length === 0} size="sm">
                {checkingAll ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Verificar Todos
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMonitors ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          ) : monitors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Eye className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No hay enlaces monitoreados</p>
              <p className="text-sm">Añade URLs arriba para comenzar a vigilarlas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {monitors.map((mon) => (
                <div key={mon.monitor_id} className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {mon.current_state?.link_found ? (
                          <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Enlace OK
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-600 border-red-500/30">
                            <XCircle className="w-3 h-3 mr-1" />
                            No encontrado
                          </Badge>
                        )}
                        {mon.current_state?.is_dofollow === true && (
                          <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            Dofollow
                          </Badge>
                        )}
                        {mon.current_state?.is_dofollow === false && (
                          <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">
                            Nofollow
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-mono truncate text-blue-600 flex items-center gap-1">
                        <a href={mon.target_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">
                          {mon.target_url}
                        </a>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-1">
                        en: <a href={mon.page_url} target="_blank" rel="noopener noreferrer" className="hover:underline">{mon.page_url}</a>
                      </p>
                      {mon.current_state?.anchor_text && (
                        <p className="text-xs text-gray-500 mt-1">
                          Anchor: "{mon.current_state.anchor_text}"
                        </p>
                      )}
                      {mon.notes && (
                        <p className="text-xs text-gray-400 mt-1 italic">{mon.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {mon.last_check ? new Date(mon.last_check).toLocaleDateString() : "Nunca"}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteMonitor(mon.monitor_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ==================== DOMAIN HEALTH CHECKER ==================== */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Estado de Dominios del Catálogo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Actions */}
          <div className="flex flex-wrap gap-3 items-center">
            <Button
              onClick={startHealthCheck}
              disabled={healthChecking}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="health-check-all-btn"
            >
              {healthChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Verificar Todo el Catálogo
            </Button>
            <input
              ref={healthFileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={uploadHealthExcel}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => healthFileRef.current?.click()}
              disabled={healthChecking}
              data-testid="health-upload-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Subir Excel de Dominios
            </Button>
            {healthSummary && (
              <Button
                variant="outline"
                onClick={() => window.open(`${API}/link-monitor/domain-health/export/${healthSummary.check_id}?filter=down`, '_blank')}
                data-testid="health-export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar Con Problemas
              </Button>
            )}
          </div>

          {/* Progress */}
          {healthChecking && healthProgress && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="font-medium text-blue-800">
                  Verificando... {healthProgress.checked} / {healthProgress.total}
                </span>
                <span className="text-sm text-red-600">({healthProgress.errors} con problemas)</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 rounded-full h-2 transition-all"
                  style={{ width: healthProgress.total > 0 ? `${(healthProgress.checked / healthProgress.total) * 100}%` : '0%' }}
                />
              </div>
            </div>
          )}

          {/* Summary */}
          {healthSummary && !healthChecking && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-slate-50">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold">{healthSummary.total}</p>
                  <p className="text-xs text-muted-foreground">Total verificados</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{healthSummary.ok}</p>
                  <p className="text-xs text-green-600">Funcionando</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{healthSummary.down}</p>
                  <p className="text-xs text-red-600">Con problemas</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{healthSummary.timestamp?.split('T')[0] || '-'}</p>
                  <p className="text-xs text-blue-600">Última verificación</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filter */}
          {healthResults.length > 0 && (
            <div className="flex gap-2">
              {[
                { value: "down", label: "Con problemas", color: "bg-red-100 text-red-700" },
                { value: "ok", label: "Funcionando", color: "bg-green-100 text-green-700" },
                { value: "all", label: "Todos", color: "bg-slate-100 text-slate-700" },
              ].map(f => (
                <Badge
                  key={f.value}
                  className={`cursor-pointer ${healthFilter === f.value ? f.color + ' ring-2 ring-offset-1' : 'bg-slate-50 text-slate-500'}`}
                  onClick={() => { setHealthFilter(f.value); loadHealthResults(f.value); }}
                >
                  {f.label}
                </Badge>
              ))}
            </div>
          )}

          {/* Results Table */}
          {healthResults.length > 0 && (
            <div className="border rounded-md">
              <div className="bg-white border-b">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm font-medium text-muted-foreground">
                      <th className="w-8 p-2">
                        {healthFilter !== "ok" && (
                          <input
                            type="checkbox"
                            checked={selectedDown.size > 0 && selectedDown.size === healthResults.filter(r => r.status !== "ok").length}
                            onChange={selectAllDown}
                            className="h-3.5 w-3.5"
                          />
                        )}
                      </th>
                      <th className="p-2">Dominio</th>
                      <th className="p-2 text-center">Estado</th>
                      <th className="p-2 text-center">HTTP</th>
                      <th className="p-2 text-right">Tiempo</th>
                      <th className="p-2">Error</th>
                      <th className="p-2">Redirección</th>
                    </tr>
                  </thead>
                </table>
              </div>
              <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                <table className="w-full text-sm">
                  <tbody>
                    {healthResults.map((r, idx) => (
                      <tr key={idx} className={`border-b hover:bg-slate-50 ${r.status !== 'ok' ? 'bg-red-50/30' : ''}`}>
                        <td className="w-8 p-2">
                          {r.status !== "ok" && (
                            <input
                              type="checkbox"
                              checked={selectedDown.has(r.domain)}
                              onChange={() => toggleDownSelection(r.domain)}
                              className="h-3.5 w-3.5"
                            />
                          )}
                        </td>
                        <td className="p-2 font-medium text-xs">{r.domain}</td>
                        <td className="p-2 text-center">
                          {r.status === "ok" ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          ) : r.status === "timeout" ? (
                            <Clock className="h-4 w-4 text-yellow-500 mx-auto" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                          )}
                        </td>
                        <td className="p-2 text-center text-xs">{r.status_code || '-'}</td>
                        <td className="p-2 text-right text-xs">{r.response_time ? `${r.response_time}ms` : '-'}</td>
                        <td className="p-2 text-xs text-red-600 max-w-[200px] truncate">{r.error || '-'}</td>
                        <td className="p-2 text-xs text-blue-600 max-w-[200px] truncate">{r.redirect_url || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Floating deactivate bar */}
          {selectedDown.size > 0 && (
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg p-3 flex items-center justify-center gap-4">
              <span className="text-sm font-medium">{selectedDown.size} dominios seleccionados</span>
              <Button
                onClick={downloadHealthSelected}
                variant="outline"
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </Button>
              <Button
                onClick={deactivateSelected}
                disabled={deactivating}
                variant="destructive"
                size="sm"
              >
                {deactivating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldAlert className="h-4 w-4 mr-2" />}
                Desactivar {selectedDown.size}
              </Button>
              <Button
                onClick={removeFromHealthList}
                disabled={removingFromList}
                variant="outline"
                size="sm"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                {removingFromList ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Eliminar del listado
              </Button>
              <Button onClick={() => setSelectedDown(new Set())} variant="outline" size="sm">
                Limpiar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LinkMonitorTab;
